import React, { useState, useEffect } from "react";
import { FaTwitter, FaLinkedin } from "react-icons/fa";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Advisors = [
  {
    id: 1,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Muthukumar",
    position: "Director",
    description:
      "Course expert with in-depth knowledge in curriculum design, ensuring high-quality educational content across various domains.",
  },
  {
    id: 2,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Kalpana",
    position: "CEO",
    description:
      "Leader and course expert focused on delivering exceptional learning experiences and managing innovative educational programs.",
  },
];

const trainers = [
  {
    id: 1,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Hariharan",
    experience: "4+ years of experience",
    position: "Corporate Trainer",
  },
  {
    id: 2,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Sadhasivam",
    experience: "3+ years of experience",
    position: "Corporate Trainer",
  },
  {
    id: 3,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Vishwa",
    experience: "3+ years of experience",
    position: "Corporate Trainer",
  },
  {
    id: 4,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Raja Priya",
    experience: "3+ years of experience",
    position: "Corporate Trainer",
  },
  {
    id: 5,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Mariselvam",
    experience: "2+ years of experience",
    position: "Corporate Trainer",
  },
  {
    id: 6,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Yogesh",
    experience: "2+ years of experience",
    position: "Corporate Trainer",
  },
  {
    id: 7,
    image: require("../../../assets/images/bgimages.webp"),
    name: "Aakash",
    experience: "2+ years of experience",
    position: "Corporate Trainer",
  },
];

const ProgramAdvisorsAndTrainers = ({ fetchCourse }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    if (currentIndex + 1 < Advisors.length) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex - 1 >= 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Trainers Slider States
  const [trainerStartIndex, setTrainerStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);

  // Responsive breakpoints handler (Compact & Multi-column)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setVisibleCount(4);
      } else if (window.innerWidth >= 992) {
        setVisibleCount(3);
      } else if (window.innerWidth >= 768) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Ensure index remains in bounds if visibility changes
  useEffect(() => {
    const maxIndex = Math.max(0, trainers.length - visibleCount);
    if (trainerStartIndex > maxIndex) {
      setTrainerStartIndex(maxIndex);
    }
  }, [visibleCount, trainerStartIndex]);

  const nextTrainers = () => {
    setTrainerStartIndex((prev) =>
      Math.min(prev + 1, Math.max(0, trainers.length - visibleCount))
    );
  };

  const prevTrainers = () => {
    setTrainerStartIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <section id="programadvisor" className="w-full">
      {/* ── Meet Our Advisors Section (Compact & Absolute Edge Navs) ── */}
      <div className="bg-[#f8fafc] py-10 px-4 sm:px-6 lg:px-8 border-b border-gray-100">
        <div className="max-w-4xl mx-auto text-center" style={{ position: "relative" }}>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 tracking-tight">
            Meet Our Advisors
          </h2>
          
          {/* Card Viewport Container with absolute side controls padding */}
          <div className="relative px-10" style={{ position: "relative" }}>
            {/* Left Chevron (Absolute Side Overlay) */}
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)" }}
              className={`w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center text-slate-500 border border-slate-100 hover:scale-105 active:scale-95 transition-all z-20 ${
                currentIndex === 0 ? "opacity-20 cursor-not-allowed" : "hover:text-blue-600"
              }`}
            >
              <ChevronLeft size={16} className="stroke-[2.5]" />
            </button>

            {/* Main Advisor Card */}
            <div className="bg-white shadow-md p-5 rounded-3xl border border-gray-100/80 flex flex-col sm:flex-row items-center gap-6 flex-1 max-w-2xl mx-auto text-left transition-all duration-300 hover:shadow-lg relative z-10">
              <img
                src={Advisors[currentIndex].image}
                alt={Advisors[currentIndex].name}
                className="rounded-2xl shadow-xs w-28 h-28 object-cover border border-gray-100 flex-shrink-0"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop";
                }}
              />
              <div className="space-y-2 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    {Advisors[currentIndex].name}
                  </h3>
                  <span className="bg-blue-50 text-[#1d72e8] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md flex-shrink-0">
                    Advisor
                  </span>
                </div>
                <h4 className="text-gray-400 font-extrabold text-[10px] uppercase tracking-wider leading-none truncate">
                  {Advisors[currentIndex].position}
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">
                  {Advisors[currentIndex].description}
                </p>

                <div className="flex space-x-3 pt-1">
                  <a
                    href="https://x.com/yslearn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-blue-500 transition-colors"
                  >
                    <FaTwitter size={15} />
                  </a>
                  <a
                    href="https://www.linkedin.com/yslearn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-blue-700 transition-colors"
                  >
                    <FaLinkedin size={15} />
                  </a>
                </div>
              </div>
            </div>

            {/* Right Chevron (Absolute Side Overlay) */}
            <button
              onClick={nextSlide}
              disabled={currentIndex === Advisors.length - 1}
              style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
              className={`w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center text-slate-500 border border-slate-100 hover:scale-105 active:scale-95 transition-all z-20 ${
                currentIndex === Advisors.length - 1 ? "opacity-20 cursor-not-allowed" : "hover:text-blue-600"
              }`}
            >
              <ChevronRight size={16} className="stroke-[2.5]" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-1.5 mt-5">
            {Advisors.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  currentIndex === index ? "bg-blue-600 w-4" : "bg-gray-300 w-1.5"
                }`}
              ></div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Program Trainers Section (Teal/Dark Compact Slider with Overlay controls) ── */}
      <div className="bg-[#0b1c35] py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5 relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl mb-8 text-left">
            <span className="pd-badge mb-3 !bg-white/10 !border-white/20 !text-blue-300">
              Expert Instructors
            </span>
            <h2 className="text-3xl font-extrabold text-white tracking-tight leading-none mt-1">
              Program Trainers
            </h2>
          </div>

          {/* Container with relative positioning inline override for correct chevron scoping */}
          <div className="relative px-8" style={{ position: "relative" }}>
            {/* Slider track viewport */}
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out py-2"
                style={{
                  transform: `translateX(-${trainerStartIndex * (100 / visibleCount)}%)`,
                }}
              >
                {trainers.map((trainer) => (
                  <div
                    key={trainer.id}
                    className="px-2 flex-shrink-0"
                    style={{ minWidth: `${100 / visibleCount}%` }}
                  >
                    {/* Compact row-based card */}
                    <div className="bg-white rounded-2xl p-4 border border-slate-100/80 flex items-center gap-4 shadow-xs hover:shadow-md transition-all duration-300 h-full hover:-translate-y-0.5">
                      <img
                        src={trainer.image}
                        alt={trainer.name}
                        className="w-14 h-14 rounded-full object-cover border border-slate-150 shadow-xs flex-shrink-0 bg-slate-50"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop";
                        }}
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="text-sm font-bold text-gray-900 truncate leading-snug">
                          {trainer.name}
                        </h3>
                        <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider leading-none mt-0.5 truncate">
                          {trainer.position}
                        </p>
                        <div className="mt-2.5 flex items-center gap-1.5">
                          <span className="bg-blue-50/80 text-[#1d72e8] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md">
                            {trainer.experience.split(" ")[0]} Exp
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Absolute navigation overlay arrows inline-styled to guarantee position boundaries */}
            {trainers.length > visibleCount && (
              <>
                <button
                  onClick={prevTrainers}
                  disabled={trainerStartIndex === 0}
                  style={{ position: "absolute", left: "-8px", top: "50%", transform: "translateY(-50%)" }}
                  className={`w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-slate-700 border border-slate-100 hover:scale-105 active:scale-95 transition-all z-20 ${
                    trainerStartIndex === 0
                      ? "opacity-25 cursor-not-allowed hover:scale-100"
                      : "hover:text-blue-600"
                  }`}
                >
                  <ChevronLeft size={18} className="stroke-[2.5]" />
                </button>

                <button
                  onClick={nextTrainers}
                  disabled={trainerStartIndex >= trainers.length - visibleCount}
                  style={{ position: "absolute", right: "-8px", top: "50%", transform: "translateY(-50%)" }}
                  className={`w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-slate-700 border border-slate-100 hover:scale-105 active:scale-95 transition-all z-20 ${
                    trainerStartIndex >= trainers.length - visibleCount
                      ? "opacity-25 cursor-not-allowed hover:scale-100"
                      : "hover:text-blue-600"
                  }`}
                >
                  <ChevronRight size={18} className="stroke-[2.5]" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramAdvisorsAndTrainers;
